<script setup lang="ts">
import { useTemplateRef } from 'vue'
import AppScrollArea from '../AppScrollArea/AppScrollArea.vue'

withDefaults(
  defineProps<{
    /** `default` pads the content 16px · `list`, a 4px-inset column of `AppPanelRow`s · `flow`, rows that wrap side by side with a 4px gap · `stack`, a 4px-inset column of cards 4px apart · `flush`, no inset, for a grid that draws its own cells · `document`, a 48px inset around running text on a sheet at least A4 tall. */
    variant?: 'default' | 'list' | 'flow' | 'stack' | 'flush' | 'document'
    /** `glass` is the frosted card · `paper` is a plain white card, for a document or a section's content. */
    surface?: 'glass' | 'paper'
    /** Takes the panel's remaining height and scrolls its own content, so the rim above stays put. */
    fill?: boolean
  }>(),
  { variant: 'default', surface: 'glass', fill: false },
)

defineSlots<{
  /** The body's content; in `list` and `flow`, `AppPanelRow`s and inset `AppSeparator` rules. */
  default(): unknown
}>()

const scroller = useTemplateRef<InstanceType<typeof AppScrollArea>>('scroller')

defineExpose({
  /** Scrolls the body's content back to its top. */
  scrollToTop(): void {
    const element = scroller.value?.element
    if (element) element.scrollTop = 0
  },
})

const SURFACE_CLASSES = { glass: 'glass-card', paper: 'bg-bg-primary' } as const

const VARIANT_CLASSES = {
  default: 'p-8',
  list: 'flex flex-col p-2',
  flow: 'flex flex-row flex-wrap gap-2 p-2',
  stack: 'flex flex-col gap-2 p-2',
  flush: '',
  document: 'aspect-a4 p-24',
} as const
</script>

<template>
  <div
    data-panel-well
    class="relative isolate min-w-0 rounded-8-in-2 bg-bg-content"
    :class="fill && 'flex min-h-0 flex-1'"
  >
    <div
      v-if="fill"
      :class="SURFACE_CLASSES[surface]"
      class="flex min-h-0 min-w-0 flex-1 flex-col rounded-8-in-2"
    >
      <AppScrollArea
        ref="scroller"
        class="min-h-0 flex-1 rounded-8-in-2"
        :class="VARIANT_CLASSES[variant]"
      >
        <slot />
      </AppScrollArea>
    </div>
    <div
      v-else
      class="min-w-0 rounded-8-in-2"
      :class="[SURFACE_CLASSES[surface], VARIANT_CLASSES[variant]]"
    >
      <slot />
    </div>
  </div>
</template>
